import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthProvider";
import { useLocalStorage } from "../lib/useLocalStorage";
import {
  apiGetCart,
  apiAddToCart,
  apiCheckout,
  apiRemoveFromCart,
  apiUpdateCartQuantity,
  apiClearCart,
  apiGuestCheckout,
  apiValidateDiscount,
} from "../lib/api";
import { CartItem, Product, CartState, AppliedDiscount } from "../lib/types";
import { toast } from "sonner";

interface CartContextType extends CartState {
  addItem: (product: Product, quantity?: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  getItemQuantity: (productId: string) => number;
  getItemCount: () => number;
  isGuest: boolean;
  guestCheckout: (
    shippingInfo: any,
    paymentMethod?: string,
    discountCodes?: string[]
  ) => Promise<string>;
  // Discount management
  appliedDiscounts: AppliedDiscount[];
  addDiscount: (discount: AppliedDiscount) => void;
  removeDiscount: (discountId: string) => void;
  validateDiscounts: () => Promise<void>;
  // Cart sidebar state
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

type CartAction =
  | { type: "ADD_ITEM"; payload: { product: Product; quantity: number } }
  | { type: "REMOVE_ITEM"; payload: string }
  | {
      type: "UPDATE_QUANTITY";
      payload: { productId: string; quantity: number };
    }
  | { type: "CLEAR_CART" }
  | { type: "LOAD_CART"; payload: CartItem[] }
  | { type: "ADD_DISCOUNT"; payload: AppliedDiscount }
  | { type: "REMOVE_DISCOUNT"; payload: string }
  | { type: "SET_DISCOUNTS"; payload: AppliedDiscount[] };

const CartContext = createContext<CartContextType | undefined>(undefined);

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case "ADD_ITEM": {
      const { product, quantity } = action.payload;
      const existingItem = state.items.find(
        (item) => item.product.id === product.id
      );

      let newItems: CartItem[];
      if (existingItem) {
        // Update existing item quantity
        newItems = state.items.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Create new cart item with product ID as cart item ID for consistency
        const newItem: CartItem = {
          id: product.id,
          product,
          quantity,
          price: product.price,
        };
        newItems = [...state.items, newItem];
      }

      const total = newItems.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );
      const itemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        items: newItems,
        total,
        itemCount,
      };
    }

    case "REMOVE_ITEM": {
      const newItems = state.items.filter(
        (item) => item.product.id !== action.payload
      );
      const total = newItems.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );
      const itemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        items: newItems,
        total,
        itemCount,
      };
    }

    case "UPDATE_QUANTITY": {
      const { productId, quantity } = action.payload;

      if (quantity <= 0) {
        return cartReducer(state, { type: "REMOVE_ITEM", payload: productId });
      }

      const newItems = state.items.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item
      );

      const total = newItems.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );
      const itemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        items: newItems,
        total,
        itemCount,
      };
    }

    case "CLEAR_CART": {
      return {
        items: [],
        total: 0,
        itemCount: 0,
      };
    }

    case "LOAD_CART": {
      const items = action.payload;
      const total = items.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );
      const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

      return {
        items,
        total,
        itemCount,
      };
    }

    default:
      return state;
  }
};

const initialState: CartState = {
  items: [],
  total: 0,
  itemCount: 0,
};

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [guestCart, setGuestCart] = useLocalStorage("guest-cart", []);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Add persistent discount storage
  const [appliedDiscounts, setAppliedDiscounts] = useLocalStorage(
    "applied-discounts",
    []
  );

  const isGuest = !user?.id;

  // Load cart from backend on mount or when user changes, or from local storage for guests
  useEffect(() => {
    setIsInitialized(false); // Reset initialization when user changes

    if (user?.id) {
      // Load authenticated user cart from backend
      apiGetCart(user.id)
        .then((items) => {
          // items now contain full product data
          const cartItems = items.map((i) => ({
            id: i.id,
            product: i.product,
            quantity: i.quantity,
            price: i.product.price,
          }));
          dispatch({ type: "LOAD_CART", payload: cartItems });
          setIsInitialized(true);
        })
        .catch(() => {
          dispatch({ type: "LOAD_CART", payload: [] });
          setIsInitialized(true);
        });
    } else {
      // Load guest cart from local storage directly to avoid dependency issues
      try {
        const savedGuestCart = JSON.parse(
          localStorage.getItem("guest-cart") || "[]"
        );
        dispatch({ type: "LOAD_CART", payload: savedGuestCart });
      } catch {
        dispatch({ type: "LOAD_CART", payload: [] });
      }
      setIsInitialized(true);
    }
  }, [user?.id]); // Remove guestCart from dependencies

  // Sync local storage for guest cart - only after initialization and when items change for guests
  useEffect(() => {
    if (isGuest && isInitialized) {
      setGuestCart(state.items);
    }
  }, [state.items, isGuest, isInitialized, setGuestCart]);

  const addItem = async (product: Product, quantity: number = 1) => {
    if (isGuest) {
      // Handle guest cart locally
      dispatch({ type: "ADD_ITEM", payload: { product, quantity } });
      let productName = product.name;
      switch (i18n.language) {
        case "es":
          productName = product.nameEs || product.name;
          break;
        case "he":
          productName = product.nameHe || product.name;
          break;
        default:
          productName = product.name;
      }
      // toast.success(`${productName} ${t("toast.addedToCart")}`);
      return;
    }

    if (!user?.id) {
      toast.error(t("toast.pleaseLogin"));
      return;
    }
    try {
      await apiAddToCart(user.id, product.id, quantity);
      dispatch({ type: "ADD_ITEM", payload: { product, quantity } });
      let productName = product.name;
      switch (i18n.language) {
        case "es":
          productName = product.nameEs || product.name;
          break;
        case "he":
          productName = product.nameHe || product.name;
          break;
        default:
          productName = product.name;
      }
      // toast.success(`${productName} ${t("toast.addedToCart")}`);
    } catch {
      toast.error(t("toast.failedToAddToCart"));
    }
  };

  const removeItem = async (productId: string) => {
    if (isGuest) {
      // Handle guest cart locally
      dispatch({ type: "REMOVE_ITEM", payload: productId });
      // toast.success(t("toast.removedFromCart"));
      return;
    }

    if (!user?.id) return;

    try {
      await apiRemoveFromCart(user.id, productId);
      dispatch({ type: "REMOVE_ITEM", payload: productId });
      // toast.success(t("toast.removedFromCart"));
    } catch {
      // toast.error(t("toast.failedToRemoveFromCart"));
    }
  };

  const updateQuantity = async (productId: string, quantity: number) => {
    if (isGuest) {
      // Handle guest cart locally
      const item = state.items.find((item) => item.product.id === productId);
      if (item && item.product.stockQuantity < quantity) {
        toast.error(t("toast.notEnoughStock"));
        return;
      }
      dispatch({ type: "UPDATE_QUANTITY", payload: { productId, quantity } });
      return;
    }

    if (!user?.id) return;

    const item = state.items.find((item) => item.product.id === productId);
    if (item && item.product.stockQuantity < quantity) {
      toast.error(t("toast.notEnoughStock"));
      return;
    }

    try {
      await apiUpdateCartQuantity(user.id, productId, quantity);
      dispatch({ type: "UPDATE_QUANTITY", payload: { productId, quantity } });
    } catch {
      toast.error(t("toast.failedToUpdateCart"));
    }
  };

  const clearCart = async () => {
    if (isGuest) {
      // Handle guest cart locally
      dispatch({ type: "CLEAR_CART" });
      setAppliedDiscounts([]); // Clear discounts when cart is cleared
      toast.success(t("toast.cartCleared"));
      return;
    }

    if (!user?.id) return;

    try {
      await apiClearCart(user.id);
      dispatch({ type: "CLEAR_CART" });
      setAppliedDiscounts([]); // Clear discounts when cart is cleared
      toast.success(t("toast.cartCleared"));
    } catch {
      // Still clear locally even if API fails
      dispatch({ type: "CLEAR_CART" });
      setAppliedDiscounts([]); // Clear discounts when cart is cleared
      toast.success(t("toast.cartCleared"));
    }
  };

  const guestCheckout = async (
    shippingInfo: any,
    paymentMethod: string = "credit_card",
    discountCodes: string[] = []
  ) => {
    if (!isGuest) {
      throw new Error("Guest checkout can only be used for guest users");
    }

    if (state.items.length === 0) {
      throw new Error("Cart is empty");
    }

    const checkoutData = {
      items: state.items.map((item) => ({
        product_id: item.product.id,
        quantity: item.quantity,
      })),
      shipping_info: shippingInfo,
      payment_method: paymentMethod,
      discountCodes,
    };

    const result = await apiGuestCheckout(checkoutData);

    if (result.success) {
      dispatch({ type: "CLEAR_CART" });
      return result.order_id.toString();
    } else {
      throw new Error("Checkout failed");
    }
  };

  const getItemQuantity = (productId: string) => {
    const item = state.items.find((item) => item.product.id === productId);
    return item ? item.quantity : 0;
  };

  const getItemCount = () => state.itemCount;

  // Discount management functions
  const addDiscount = (discount: AppliedDiscount) => {
    // Check if discount is already applied
    const existingDiscount = appliedDiscounts.find((d) => d.id === discount.id);
    if (!existingDiscount) {
      setAppliedDiscounts((prev) => [...prev, discount]);
      toast.success(t("discounts.applied"));
    }
  };

  const removeDiscount = (discountId: string) => {
    setAppliedDiscounts((prev) => prev.filter((d) => d.id !== discountId));
    toast.success(t("discounts.removed"));
  };

  // Validate discounts against current cart state
  const validateDiscounts = async () => {
    if (appliedDiscounts.length === 0) return;

    const cartItems = state.items.map((item) => ({
      product_id: item.product.id,
      quantity: item.quantity,
    }));

    const total = state.total;
    const invalidDiscounts: string[] = [];

    for (const discount of appliedDiscounts) {
      try {
        const response = await apiValidateDiscount(
          discount.code,
          cartItems,
          total
        );

        if (!response.valid) {
          invalidDiscounts.push(discount.id);
          toast.error(t("discounts.noLongerValid", { name: discount.name }));
        }
      } catch (error) {
        invalidDiscounts.push(discount.id);
        toast.error(t("discounts.validationError", { name: discount.name }));
      }
    }

    // Remove invalid discounts
    if (invalidDiscounts.length > 0) {
      setAppliedDiscounts((prev) =>
        prev.filter((d) => !invalidDiscounts.includes(d.id))
      );
    }
  };

  // Validate discounts when cart changes
  useEffect(() => {
    if (isInitialized && appliedDiscounts.length > 0) {
      validateDiscounts();
    }
  }, [state.items, state.total, isInitialized]);

  const value: CartContextType = {
    ...state,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getItemQuantity,
    getItemCount,
    isGuest,
    guestCheckout,
    appliedDiscounts,
    addDiscount,
    removeDiscount,
    validateDiscounts,
    isOpen,
    setIsOpen,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
